import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Alert,
    Typography,
    Paper,
    IconButton,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Divider,
    FormHelperText,
    Tooltip,
    Menu,
    ListItemText,
} from "@mui/material";
import {
    Edit,
    Delete,
    Download,
    ArrowBackIos,
    ArrowForwardIos,
    ViewColumn,
    DensityMedium,
    Search
} from "@mui/icons-material"; import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import { useEffect, useState, useMemo, useCallback } from "react";
import ContratService from "../../api/services/ContratService";
import SocieteService from "../../api/services/SocieteService";
import { styled, alpha } from "@mui/material/styles";
import jsPDF from "jspdf";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { Snackbar, Slide } from "@mui/material";
import { useLocation } from "react-router-dom";

const StyledCard = styled(Paper)(({ theme }) => ({
    background: `linear-gradient(135deg, ${alpha("#2a5fa9", 0.05)} 0%, ${alpha("#65b986", 0.05)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderRadius: "16px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: `0 20px 40px ${alpha("#2a5fa9", 0.1)}`,
        border: `1px solid ${alpha("#2a5fa9", 0.2)}`,
    },
    padding: "24px",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: '16px',
    overflowX: 'auto',
    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    boxShadow: `0 8px 32px ${alpha('#000000', 0.1)}`,
    '& .MuiTableContainer-root': {
        overflowX: 'auto',
        whiteSpace: 'nowrap',
    },
    '&::-webkit-scrollbar': {
        height: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: alpha(theme.palette.divider, 0.1),
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: alpha('#2a5fa9', 0.3),
        borderRadius: '4px',
        '&:hover': {
            background: alpha('#2a5fa9', 0.5),
        },
    },
    [theme.breakpoints.down('sm')]: {
        '& .MuiTableCell-root': {
            padding: '8px',
            fontSize: '0.75rem',
        },
        '& .MuiTableCell-head': {
            padding: '8px',
            fontSize: '0.7rem',
        },
    },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    background: `linear-gradient(90deg, 
    #2a5fa9 0%, 
    #3a7fb5 20%,
    #4a9fc0 40%, 
    #5ab8a0 60%,
    #70bd8d 80%, 
    #8fc68b 100%)`,
    '& .MuiTableCell-head': {
        color: '#ffffff',
        fontWeight: 600,
        fontSize: '0.9rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: 'none',
        padding: '20px 16px',
        whiteSpace: 'nowrap',
    },
}));



const StyledTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
    padding: '16px',
    '&:nth-of-type(even)': {
        backgroundColor: alpha(theme.palette.action.hover, 0.02),
    },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    background: `linear-gradient(135deg, #2a5fa9 0%, #65b986 100%)`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 700,
    fontSize: '1.5rem',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    background: alpha('#2a5fa9', 0.1),
    color: '#2a5fa9',
    '&:hover': {
        background: alpha('#2a5fa9', 0.2),
        transform: 'scale(1.1)',
    },
    '&:disabled': {
        background: alpha(theme.palette.action.disabled, 0.1),
        color: theme.palette.action.disabled,
    },
    transition: 'all 0.2s ease',
}));

const getChipStyles = (theme) => ({
    backgroundColor: alpha('#4cbed8', 0.1),
    color: '#2a5fa9',
    border: `1px solid ${alpha('#4cbed8', 0.3)}`,
    fontWeight: 600,
    fontSize: '0.75rem',
    borderRadius: '8px',
    margin: '2px',
    '&:hover': {
        backgroundColor: alpha('#4cbed8', 0.2),
    },
});

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    border: "none",
    borderRadius: "16px",
    background: `linear-gradient(135deg,
    ${alpha(theme.palette.background.paper, 0.9)} 0%,
    ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
    backdropFilter: "blur(10px)",
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
    width: "100%",
    height: "100%",
    "& .MuiDataGrid-main": {
        overflow: "visible",
    },
    "& .MuiDataGrid-columnHeaders": {
        background: `linear-gradient(90deg,
      #2a5fa9 0%, #3a7fb5 20%, #4a9fc0 40%,
      #5ab8a0 60%, #70bd8d 80%, #8fc68b 100%)`,
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "0.875rem",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        borderBottom: "none",
        minHeight: "56px",
        overflow: "visible",
    },
    "& .MuiDataGrid-cell": {
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
        padding: "16px",
        "&:nth-of-type(even)": {
            backgroundColor: alpha(theme.palette.action.hover, 0.02),
        },
    },
    "& .MuiDataGrid-footerContainer": {
        borderTop: "none",
        background: `linear-gradient(135deg, #fcfdff 0%, #f6f9fa 100%)`,
        color: theme.palette.text.primary,
    },
    "& .MuiCheckbox-root": {
        color: `${theme.palette.primary.main} !important`,
    },
    "& .MuiDataGrid-virtualScroller": {
        overflowX: "auto",
        "&::-webkit-scrollbar": {
            height: "8px",
        },
        "&::-webkit-scrollbar-track": {
            background: alpha(theme.palette.divider, 0.1),
            borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
            background: alpha("#2a5fa9", 0.3),
            borderRadius: "4px",
            "&:hover": {
                background: alpha("#2a5fa9", 0.5),
            },
        },
    },
    [theme.breakpoints.down("sm")]: {
        "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
            padding: "8px",
            fontSize: "0.75rem",
        },
    },
}));

const etatsDisponibles = [
    "A renouveler",
    "Ne pas renouveler",
    "Résilié",
    "Préavis en cours",
    "En-cours",
    "a étudier",
];

const periodicitesPaiement = ["Mensuel", "Trimestriel", "Semestriel", "Annuel"];
const tauxTVAOptions = ["20", "19", "10", "5.5", "0"];
const CustomToolbar = ({ setAnchorElColumns, setAnchorElDensity }) => {
    const theme = useTheme();
    return (
        <Box sx={{
            p: 1, display: "flex", alignItems: "center",
            background: alpha(theme.palette.background.paper, 0.8),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
            <Box display="flex" gap={1}>
                <Tooltip title="Colonnes">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton onClick={(e) => setAnchorElColumns(e.currentTarget)} size="small">
                            <ViewColumn fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#000000', userSelect: 'none' }}>
                            Colonnes
                        </Typography>
                    </Box>
                </Tooltip>
                <Tooltip title="Densité">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton onClick={(e) => setAnchorElDensity(e.currentTarget)} size="small">
                            <DensityMedium fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#000000', userSelect: 'none' }}>
                            Densité
                        </Typography>
                    </Box>
                </Tooltip>
            </Box>
        </Box>
    );
};
const Contrats = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [columnSearch, setColumnSearch] = useState("");
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        try {
            const saved = localStorage.getItem("contrats_column_visibility");
            return saved ? JSON.parse(saved) : {};
        } catch { return {}; }
    });

    const [density, setDensity] = useState(() => {
        return localStorage.getItem("contrats_density") || 'standard';
    });
    const userRole = localStorage.getItem("user_role");
    const token = localStorage.getItem("token");
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const [isLoading, setIsLoading] = useState(true);
    const [contrats, setContrats] = useState([]);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openLetter, setOpenLetter] = useState(false);
    const [selectedContrat, setSelectedContrat] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [firstErrorField, setFirstErrorField] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [uniqueSocietes, setUniqueSocietes] = useState([]);
    const [newFichierPdf, setNewFichierPdf] = useState(null);
    const [editFichierPdf, setEditFichierPdf] = useState(null);
    const [allSocietes, setAllSocietes] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [anchorElColumns, setAnchorElColumns] = useState(null);
    const [anchorElDensity, setAnchorElDensity] = useState(null);
    const [comptesComptables, setComptesComptables] = useState([]);
    const [loadingComptes, setLoadingComptes] = useState(false);
    const [comptesComptablesEdit, setComptesComptablesEdit] = useState([]);
    const [loadingComptesEdit, setLoadingComptesEdit] = useState(false);
    const [customCompteComptableAdd, setCustomCompteComptableAdd] = useState(false);
    const [customCompteComptableEdit, setCustomCompteComptableEdit] = useState(false);
    const [newContrat, setNewContrat] = useState({
        n_contact: "",
        nom_fournisseur: "",
        code_fournisseur: "",
        nom_contact: "",
        telephone_contact: "",
        email_contact: "",
        adresse_fournisseur: "",
        cp_fournisseur: "",
        ville_fournisseur: "",
        pays_fournisseur: "",
        compte_comptable: "",
        compte_comptable_id: "",
        compte_comptable_manuel: "",
        etat: "",
        date_signature: "",
        duree: "",
        fin_contrat: "",
        delai_preavis: "",
        date_preavis: "",
        periodicite_paiement: "",
        periode_reengagement: "12",
        loyer_ht: "",
        devise: "",
        taux_tva_loyer: "",
        societe_id: "",

    });
    const [filterSociete, setFilterSociete] = useState("");
    useEffect(() => {
        if (contrats.length > 0) {
            const societesMap = new Map();
            contrats.forEach((c) => {
                if (c.societe_id && c.societe_nom) {
                    societesMap.set(c.societe_id, { id: c.societe_id, nom: c.societe_nom });
                }
            });
            setUniqueSocietes(Array.from(societesMap.values()));
        }
    }, [contrats]);

    useEffect(() => {
        localStorage.setItem("contrats_column_visibility", JSON.stringify(columnVisibilityModel));
    }, [columnVisibilityModel]);

    useEffect(() => {
        localStorage.setItem("contrats_density", density);
    }, [density]);

    const [letterContent, setLetterContent] = useState({
        recipientName: "",
        recipientContact: "",
        recipientAddress: "",
        recipientCP: "",
        recipientCity: "",
        recipientCountry: "",
        subject: "",
        body: "",
        sender: userData.name || "",
    });
    const [filterPeriodicite, setFilterPeriodicite] = useState("");
    const [error, setError] = useState("");

    const fetchAllSocietes = async () => {
        try {
            const data = await SocieteService.getAll(token);
            setAllSocietes(data);
        } catch (err) {
            console.error("Erreur sociétés:", err);
        }
    };

    useEffect(() => {
        if (token) {
            fetchAllSocietes();
        }
    }, [token]);
    const fetchContrats = async () => {
        if (!token) {
            console.error("Utilisateur non authentifié");
            setError("Utilisateur non authentifié.");
            return;
        }
        setIsLoading(true);
        try {
            const contratsData = await ContratService.getContrats(userRole, token);

            if (!Array.isArray(contratsData)) {
                console.error("Les données des contrats ne sont pas un tableau:", contratsData);
                setError("Données mal formatées ou vides.");
                return;
            }

            setContrats(contratsData);
            setError("");
        } catch (err) {
            console.error("Erreur lors de la récupération des contrats:", err);
            setError(err.response?.data?.message || "Erreur lors de la récupération des contrats.");
        }

        finally {
            setIsLoading(false);
        }
    };

    const location = useLocation();

    // Highlight de la ligne venant des alertes
    const [highlightId, setHighlightId] = useState(null);
    const [filterFromAlerte, setFilterFromAlerte] = useState(false);
    const [alerteFilterNom, setAlerteFilterNom] = useState("");

    // Highlight + Filtre depuis les alertes
    useEffect(() => {
        if (!location.state?.highlightId || contrats.length === 0) return;

        const id = location.state.highlightId;
        const nomContrat = location.state.highlightItem;

        // Réinitialiser les filtres manuels
        setFilterSociete("");
        setFilterPeriodicite("");
        setColumnSearch("");

        if (nomContrat) {
            // Mode filtre alerte
            setAlerteFilterNom(nomContrat);
            setFilterFromAlerte(true);
            setHighlightId(id);
        } else {
            setHighlightId(id);
            setFilterFromAlerte(false);
        }
    }, [location.state, contrats]);

    const handleDownloadExcel = () => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const timestamp = `${pad(now.getDate())}_${pad(now.getMonth() + 1)}_${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
        const fileName = `contrats_${timestamp}.csv`;

        const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");
        const fmtNum = (n) =>
            n !== null && n !== undefined && n !== "" ? String(n).replace(".", ",") : "";

        const headers = [
            "Société",
            "N° Contrat",
            "Compte Comptable",
            "Nom Fournisseur",
            "Code Fournisseur",
            "Nom Contact",
            "Téléphone Contact",
            "Email Contact",
            "Adresse Fournisseur",
            "CP Fournisseur",
            "Ville Fournisseur",
            "Pays Fournisseur",
            "État",
            "Date Signature",
            "Durée (mois)",
            "Fin Contrat",
            "Délai Préavis (mois)",
            "Date Préavis",
            "Période Réengagement (mois)",
            "Périodicité Paiement",
            "Loyer HT",
            "Devise",
            "Taux TVA Loyer (%)",
            "Reconduction Tacite",
            "Détails / Objets du Contrat",
        ];

        // On exporte ce qui est affiché (filtres + recherche inclus)
        const data = filteredBySearch.map((row) => {
            const datePreavis =
                row.date_preavis ||
                calculateDatePreavis(
                    row.fin_contrat ? new Date(row.fin_contrat).toISOString().split("T")[0] : "",
                    row.delai_preavis
                );

            return [
                row.societe_nom || "",
                row.n_contact || "",
                row.compte_comptable || "",
                row.nom_fournisseur || "",
                row.code_fournisseur || "",
                row.nom_contact || "",
                row.telephone_contact || "",
                row.email_contact || "",
                row.adresse_fournisseur || "",
                row.cp_fournisseur || "",
                row.ville_fournisseur || "",
                row.pays_fournisseur || "",
                row.etat || "",
                fmtDate(row.date_signature),
                row.duree ?? "",
                fmtDate(row.fin_contrat),
                row.delai_preavis ?? "",
                fmtDate(datePreavis),
                row.periode_reengagement ?? "",
                row.periodicite_paiement || "",
                fmtNum(row.loyer_ht),
                row.devise || "",
                fmtNum(row.taux_tva_loyer),
                row.reconduction_tacite || "",
                row.details || "",
            ];
        });

        const csvContent =
            "\uFEFF" +
            [
                headers.map((h) => `"${h}"`).join(";"),
                ...data.map((row) =>
                    row
                        .map((cell) => `"${String(cell).replace(/"/g, '""').replace(/\n|\r/g, " ")}"`)
                        .join(";")
                ),
            ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        if (userRole === "gestionnaire") {
            const societesDuProfil = userData?.societes || [];
            if (societesDuProfil.length > 0) {
                setUniqueSocietes(societesDuProfil.map(s => ({ id: s.id, nom: s.nom })));
            } else if (contrats.length > 0) {
                const societesMap = new Map();
                contrats.forEach((c) => {
                    if (c.societe_id && c.societe_nom) {
                        societesMap.set(c.societe_id, { id: c.societe_id, nom: c.societe_nom });
                    }
                });
                setUniqueSocietes(Array.from(societesMap.values()));
            }
        }
    }, [contrats, userRole]);

    useEffect(() => {
        if (userRole === "gestionnaire" && uniqueSocietes.length === 1) {
            setNewContrat(prev => ({ ...prev, societe_id: uniqueSocietes[0].id }));
        }
    }, [uniqueSocietes, userRole]); useEffect(() => {
        if (userRole === "gestionnaire" && uniqueSocietes.length === 1) {
            setNewContrat(prev => ({ ...prev, societe_id: uniqueSocietes[0].id }));
        }
    }, [uniqueSocietes, userRole]);

    useEffect(() => {
        fetchContrats();
    }, [token, userRole]);

    const chargerComptesComptables = async (societeId, isEdit = false) => {
        const setList = isEdit ? setComptesComptablesEdit : setComptesComptables;
        const setLoading = isEdit ? setLoadingComptesEdit : setLoadingComptes;
        if (!societeId) { setList([]); return; }
        setLoading(true);
        try {
            const data = await ContratService.getComptesComptables(userRole, token, societeId);
            setList(Array.isArray(data) ? data : []);
        } catch {
            setList([]);
            setSnackbarMessage("Impossible de charger le plan comptable pour cette société.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    // Ajout (couvre aussi le gestionnaire dont la société est auto-sélectionnée)
    useEffect(() => {
        chargerComptesComptables(newContrat.societe_id, false);
    }, [newContrat.societe_id]);

    // Modification
    useEffect(() => {
        if (openEdit) chargerComptesComptables(selectedContrat?.societe_id, true);
    }, [openEdit, selectedContrat?.societe_id]);

    // Fonction de validation pour les contrats
    const validateContrat = (data, isEdit = false) => {
        const errors = {};

        if (!data.n_contact?.toString().trim()) {
            errors.n_contact = "Le numéro de contact est requis.";
        }
        // Champs obligatoires pour tous les contrats
        if (!data.compte_comptable_id && !data.compte_comptable_manuel?.toString().trim()) {
            errors.compte_comptable_id = "Le compte comptable est requis.";
        }

        if (!data.societe_id?.toString().trim() && userRole === "admin") {
            errors.societe_id = "La société est requise.";
        }


        if (!data.date_signature?.toString().trim()) {
            errors.date_signature = "La date de signature est requise.";
        }

        if (!data.nom_fournisseur?.toString().trim()) {
            errors.nom_fournisseur = "Le nom du fournisseur est requis.";
        }

        if (!data.adresse_fournisseur?.toString().trim()) {
            errors.adresse_fournisseur = "L'adresse du fournisseur est requise.";
        }

        if (!data.cp_fournisseur?.toString().trim()) {
            errors.cp_fournisseur = "Le code postal du fournisseur est requis.";
        }
        if (!data.ville_fournisseur?.toString().trim()) {
            errors.ville_fournisseur = "La ville du fournisseur est requise.";
        }
        if (!data.pays_fournisseur?.toString().trim()) {
            errors.pays_fournisseur = "Le pays du fournisseur est requis.";
        }

        if (!data.duree?.toString().trim()) {
            errors.duree = "La durée est requise.";
        }

        if (!data.taux_tva_loyer?.toString().trim()) {
            errors.taux_tva_loyer = "Le taux TVA est requis.";
        }

        if (!data.reconduction_tacite || !["oui", "non"].includes(data.reconduction_tacite.toLowerCase())) {
            errors.reconduction_tacite = "La reconduction tacite doit être Oui ou Non.";
        }

        return errors;
    };

    useEffect(() => {
        if (firstErrorField) {
            const timer = setTimeout(() => {
                const element = document.getElementById(firstErrorField);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setTimeout(() => element.focus({ preventScroll: true }), 300);
                    const originalStyle = element.style.boxShadow;
                    element.style.transition = 'all 0.3s ease';
                    element.style.boxShadow = '0 0 0 2px rgba(244, 67, 54, 0.5)';
                    setTimeout(() => {
                        if (element) element.style.boxShadow = originalStyle;
                    }, 2000);
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [firstErrorField]);

    // Updated Date Calculation Functions
    const calculateFinContrat = (dateSignature, duree) => {
        if (!dateSignature || !duree) return "";
        const date = new Date(dateSignature);
        if (isNaN(date.getTime())) return "";
        date.setMonth(date.getMonth() + Number(duree));
        if (isNaN(date.getTime())) return "";
        return date.toISOString().split("T")[0];
    };

    const calculateDatePreavis = (finContrat, delaiPreavis) => {
        if (!finContrat || !delaiPreavis) return "";
        const date = new Date(finContrat);
        if (isNaN(date.getTime())) return "";
        date.setMonth(date.getMonth() - Number(delaiPreavis));
        if (isNaN(date.getTime())) return "";
        return date.toISOString().split("T")[0];
    };

    const handleEditClick = (contrat) => {
        if (!contrat) return;

        const finContrat = contrat.fin_contrat
            ? new Date(contrat.fin_contrat).toISOString().split("T")[0]
            : calculateFinContrat(contrat.date_signature, contrat.duree);

        const datePreavis = contrat.date_preavis
            ? new Date(contrat.date_preavis).toISOString().split("T")[0]
            : calculateDatePreavis(finContrat, contrat.delai_preavis);

        setSelectedContrat({
            ...contrat,
            societe_id: contrat.societe_id || "",
            date_signature: contrat.date_signature
                ? new Date(contrat.date_signature).toISOString().split("T")[0]
                : "",
            fin_contrat: finContrat || "",
            date_preavis: datePreavis || "",
            reconduction_tacite: contrat.reconduction_tacite?.toLowerCase() === "oui" ? "oui" : "non",
            periode_reengagement: contrat.periode_reengagement || "12",
            compte_comptable_id: contrat.compte_comptable_id || "",
            compte_comptable_manuel: contrat.compte_comptable_id ? "" : (contrat.compte_comptable || ""),
        });
        setCustomCompteComptableEdit(
            !contrat.compte_comptable_id && !!contrat.compte_comptable?.toString().trim()
        );
        setEditFichierPdf(null);
        setOpenEdit(true);
    };
    const handleDeleteClick = (contrat) => {
        setSelectedContrat(contrat);
        setOpenDelete(true);
    };

    const handleLetterClick = (contrat) => {
        const currentDate = new Date().toLocaleDateString("fr-FR");
        setSelectedContrat(contrat);
        setLetterContent({
            recipientName: contrat.nom_fournisseur || "",
            recipientContact: contrat.nom_contact || "",
            recipientAddress: contrat.adresse_fournisseur || "",
            recipientCP: contrat.cp_fournisseur || "",
            recipientCity: contrat.ville_fournisseur || "",
            recipientCountry: contrat.pays_fournisseur || "",
            subject: `Résiliation du contrat référencé : ${contrat.n_contact}`,
            body: `Madame, Monsieur,\n\nPar la présente, nous vous informons de notre souhait de résilier le contrat cité en objet signé le ${contrat.date_signature ? new Date(contrat.date_signature).toLocaleDateString("fr-FR") : ""}, conformément aux dispositions contractuelles.\n\nLe contrat prévoit un préavis de ${contrat.delai_preavis} mois, débutant le ${contrat.date_preavis ? new Date(contrat.date_preavis).toLocaleDateString("fr-FR") : ""}, ce qui fixe la date effective de fin du contrat au ${contrat.fin_contrat ? new Date(contrat.fin_contrat).toLocaleDateString("fr-FR") : ""}.\n\nJe vous remercie de bien vouloir accuser réception de cette demande et de nous confirmer par écrit la prise en compte de cette résiliation à la date indiquée.\n\nJe reste à disposition pour organiser, le cas échéant, les modalités de clôture du contrat.\n\nVeuillez agréer, Madame, Monsieur, l'expression de mes salutations distinguées`,
            sender: userData.name || "",
        });
        setOpenLetter(true);
    };

    const handleEditSubmit = async () => {
        const errors = validateContrat(selectedContrat, true);
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            const firstErrorKey = Object.keys(errors)[0];
            setFirstErrorField(firstErrorKey);
            setSnackbarMessage(errors[firstErrorKey]);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        try {
            const role = localStorage.getItem("user_role");
            const token = localStorage.getItem("token");
            const formData = new FormData();

            const fieldsToSend = {
                n_contact: selectedContrat.n_contact || "",
                nom_fournisseur: selectedContrat.nom_fournisseur || "",
                code_fournisseur: selectedContrat.code_fournisseur || "",
                nom_contact: selectedContrat.nom_contact || "",
                telephone_contact: selectedContrat.telephone_contact || "",
                email_contact: selectedContrat.email_contact || "",
                adresse_fournisseur: selectedContrat.adresse_fournisseur || "",
                cp_fournisseur: selectedContrat.cp_fournisseur || "",
                ville_fournisseur: selectedContrat.ville_fournisseur || "",
                pays_fournisseur: selectedContrat.pays_fournisseur || "",
                compte_comptable: selectedContrat.compte_comptable_id ? "" : (selectedContrat.compte_comptable_manuel || ""),
                compte_comptable_id: selectedContrat.compte_comptable_id || "",
                etat: selectedContrat.etat || "",
                date_signature: selectedContrat.date_signature || "",
                duree: selectedContrat.duree ? Number(selectedContrat.duree) : "",
                fin_contrat: selectedContrat.fin_contrat || "",
                delai_preavis: selectedContrat.delai_preavis ? Number(selectedContrat.delai_preavis) : "",
                date_preavis: selectedContrat.date_preavis || "",
                periodicite_paiement: selectedContrat.periodicite_paiement || "",
                periode_reengagement: selectedContrat.periode_reengagement ? Number(selectedContrat.periode_reengagement) : 12,
                loyer_ht: selectedContrat.loyer_ht ? Number(selectedContrat.loyer_ht) : 0,
                devise: selectedContrat.devise || "",
                taux_tva_loyer: selectedContrat.taux_tva_loyer ? Number(selectedContrat.taux_tva_loyer) : 0,
                details: selectedContrat.details || "",
                reconduction_tacite: selectedContrat.reconduction_tacite || "non",
                societe_id: selectedContrat.societe_id || "",
                _method: "PUT",
            };

            Object.entries(fieldsToSend).forEach(([key, value]) => {
                if (value !== undefined) {
                    formData.append(key, value);
                }
            });

            if (editFichierPdf instanceof File) {
                formData.append("fichier_pdf", editFichierPdf);
            }

            setIsUploading(true);
            setUploadProgress(0);

            await ContratService.updateContrat(role, token, selectedContrat.id, formData, (percent) => {
                setUploadProgress(percent);
            })
            setIsUploading(false);
            setUploadProgress(0);
            setOpenEdit(false);
            setFormErrors({});
            setFirstErrorField("");
            setEditFichierPdf(null);
            setSnackbarMessage("Contrat modifié avec succès !");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            await fetchContrats();
        } catch (err) {
            console.error("Erreur mise à jour contrat :", err);
            setIsUploading(false);
            setUploadProgress(0);

            let errorMessage = "Erreur lors de la modification du contrat.";
            const translations = {
                "The n contact has already been taken.": "Ce numéro de contact existe déjà.",
                "The duree field must be at least 1.": "La durée doit être au minimum de 1.",
                "The duree field is required.": "La durée est obligatoire.",
                "The loyer ht field is required.": "Le loyer HT est obligatoire.",
                "The fichier pdf field must be a file of type: pdf.": "Le fichier doit être au format PDF.",
                "The fichier pdf failed to upload.": "Le fichier ne doit pas dépasser 2 Mo.",
                "The fichier pdf failed to upload": "Le fichier ne doit pas dépasser 2 Mo.",
                "fichier_pdf failed to upload": "Le fichier ne doit pas dépasser 2 Mo.",
                "failed to upload": "Le fichier ne doit pas dépasser 2 Mo.",
            };

            if (err.response?.status === 422 && err.response.data?.errors) {
                const backendErrors = err.response.data.errors;
                const translatedErrors = Object.values(backendErrors)
                    .flat()
                    .map(msg => translations[msg.trim()] || translations[msg.trim().replace(/\.$/, '')] || msg);
                errorMessage = translatedErrors.join(" • ") || errorMessage;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }

            setFormErrors({});
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const role = localStorage.getItem("user_role");
            const token = localStorage.getItem("token");

            await ContratService.deleteContrat(role, token, selectedContrat.id);

            setOpenDelete(false);
            setError("");
            await fetchContrats();
        } catch (err) {
            const errorMessage = err.response?.data?.message || "Erreur lors de la suppression du contrat.";
            setError(errorMessage);
        }
    };

    const handleAddSubmit = async () => {
        const errors = validateContrat(newContrat, false);
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            const firstErrorKey = Object.keys(errors)[0];
            setFirstErrorField(firstErrorKey);
            setSnackbarMessage(errors[firstErrorKey]);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        try {
            const role = localStorage.getItem("user_role");
            const token = localStorage.getItem("token");
            if (!token) {
                setSnackbarMessage("Utilisateur non authentifié.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return;
            }

            const finContrat = calculateFinContrat(newContrat.date_signature, newContrat.duree);
            const datePreavis = calculateDatePreavis(finContrat, newContrat.delai_preavis);
            const contratToAdd = new FormData();
            const { fichier_pdf: _ignored, ...newContratSansPdf } = newContrat;
            const fields = {
                ...newContratSansPdf,
                compte_comptable: newContrat.compte_comptable_id ? "" : (newContrat.compte_comptable_manuel || ""),
                compte_comptable_id: newContrat.compte_comptable_id || "",
                societe_id: newContrat.societe_id || null,
                loyer_ht: Number(newContrat.loyer_ht) || 0,
                duree: newContrat.duree ? Number(newContrat.duree) : null,
                delai_preavis: newContrat.delai_preavis ? Number(newContrat.delai_preavis) : null,
                taux_tva_loyer: newContrat.taux_tva_loyer ? Number(newContrat.taux_tva_loyer) : 0,
                date_signature: newContrat.date_signature || null,
                fin_contrat: newContrat.fin_contrat || null,
                date_preavis: newContrat.date_preavis || null,
                periode_reengagement: newContrat.periode_reengagement ? Number(newContrat.periode_reengagement) : 12,
            };
            Object.entries(fields).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== "") {
                    contratToAdd.append(key, value);
                }
            });
            if (newFichierPdf instanceof File) {
                contratToAdd.append("fichier_pdf", newFichierPdf);
            }

            setIsUploading(true);
            setUploadProgress(0);

            await ContratService.createContrat(role, token, contratToAdd, (percent) => {
                setUploadProgress(percent);
            });

            setIsUploading(false);
            setUploadProgress(0);
            setOpenAdd(false);
            setNewContrat({
                n_contact: "", nom_fournisseur: "", code_fournisseur: "", nom_contact: "",
                telephone_contact: "", email_contact: "", adresse_fournisseur: "", cp_fournisseur: "",
                ville_fournisseur: "", pays_fournisseur: "", etat: "",
                date_signature: "", duree: "", fin_contrat: "", delai_preavis: "", date_preavis: "",
                periodicite_paiement: "", periode_reengagement: "12", loyer_ht: "", devise: "",
                taux_tva_loyer: "", details: "", reconduction_tacite: "non", societe_id: "",
            });
            setNewFichierPdf(null);
            setFormErrors({});
            setFirstErrorField("");
            setSnackbarMessage("Contrat ajouté avec succès !");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            await fetchContrats();

        } catch (err) {
            setIsUploading(false);
            setUploadProgress(0);

            let errorMessage = "Erreur lors de l'ajout du contrat.";
            const translations = {
                "The n contact has already been taken.": "Ce numéro de contact existe déjà.",
                "The duree field must be at least 1.": "La durée doit être au minimum de 1.",
                "The loyer ht field is required.": "Le loyer HT est obligatoire.",
                "The fichier pdf field must be a file of type: pdf.": "Le fichier doit être au format PDF.",
            };
            if (err.response?.status === 422 && err.response.data?.errors) {
                const backendErrors = err.response.data.errors;
                const translatedErrors = Object.values(backendErrors).flat()
                    .map(msg => translations[msg] || msg);
                errorMessage = translatedErrors.join(", ");
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleViewPdf = async (contrat) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/${userRole === 'admin' ? 'admin' : 'gestionnaire'
                }/contrat/pdf/${contrat.id}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 401) {
                localStorage.clear();
                window.location.href = '/login';
                return;
            }

            if (!response.ok) {
                throw new Error('Fichier introuvable');
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            window.open(blobUrl, '_blank');

            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);

        } catch (err) {
            console.error('Erreur téléchargement PDF:', err);
        }
    };



    const handleDownloadContractPDF = (contrat) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 20;
        const contentWidth = pageWidth - (2 * margin);
        let yPos = margin;

        // Fonction pour vérifier et ajouter une nouvelle page si nécessaire
        const checkPageBreak = (requiredSpace = 20) => {
            if (yPos > pageHeight - margin - requiredSpace) {
                doc.addPage();
                yPos = margin;
                return true;
            }
            return false;
        };

        // Fonction pour ajouter une ligne de séparation
        const addSeparator = () => {
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 10;
        };

        // ==================== EN-TÊTE ====================
        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.text("CONTRAT DE FOURNITURE", pageWidth / 2, 15, { align: "center" });

        // Informations du contrat dans l'en-tête
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        let headerInfo = [];
        if (contrat.n_contact) headerInfo.push(`N° ${contrat.n_contact}`);
        if (contrat.date_signature) {
            headerInfo.push(`Signé le ${new Date(contrat.date_signature).toLocaleDateString("fr-FR")}`);
        }
        if (headerInfo.length > 0) {
            doc.text(headerInfo.join(" • "), pageWidth / 2, 25, { align: "center" });
        }

        doc.setTextColor(0, 0, 0);
        yPos = 45;

        // ==================== PARTIES ====================
        doc.setFillColor(245, 245, 245);
        doc.rect(margin - 5, yPos - 5, contentWidth + 10, 8, 'F');

        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(41, 128, 185);
        doc.text("PARTIES CONTRACTANTES", margin, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 12;

        // Fournisseur
        const fournisseurStartY = yPos;
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text("LE FOURNISSEUR", margin + 3, yPos);
        yPos += 7;

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        if (contrat.nom_fournisseur) {
            doc.setFont("helvetica", "bold");
            doc.text(contrat.nom_fournisseur, margin + 3, yPos);
            doc.setFont("helvetica", "normal");
            yPos += 5;
        }
        if (contrat.nom_contact) {
            doc.text(`Contact : ${contrat.nom_contact}`, margin + 3, yPos);
            yPos += 5;
        }
        if (contrat.adresse_fournisseur || contrat.cp_fournisseur || contrat.ville_fournisseur) {
            const adresse = [
                contrat.adresse_fournisseur,
                contrat.cp_fournisseur,
                contrat.ville_fournisseur,
                contrat.pays_fournisseur
            ].filter(Boolean).join(", ");
            const lines = doc.splitTextToSize(adresse, contentWidth / 2 - 10);
            lines.forEach(line => {
                doc.text(line, margin + 3, yPos);
                yPos += 4;
            });
        }
        yPos += 3;

        // Acheteur
        const acheteurX = pageWidth / 2 + 5;
        const acheteurStartY = fournisseurStartY;
        let acheteurY = acheteurStartY;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("L'ACHETEUR", acheteurX + 3, acheteurY);
        acheteurY += 7;

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("GROUPE SOBER SAS", acheteurX + 3, acheteurY);
        acheteurY += 5;

        doc.setFont("helvetica", "normal");
        const acheteurAdresse = doc.splitTextToSize(
            "391 rue de l'Avenir - Z.I. des Vernailles Ouest, 69830 SAINT GEORGES DE RENEINS",
            contentWidth / 2 - 10
        );
        acheteurAdresse.forEach(line => {
            doc.text(line, acheteurX + 3, acheteurY);
            acheteurY += 4;
        });
        acheteurY += 3;

        yPos = Math.max(yPos, acheteurY) + 10;
        checkPageBreak(50);


        // ==================== CONDITIONS CONTRACTUELLES ====================
        doc.setFillColor(245, 245, 245);
        doc.rect(margin - 5, yPos - 5, contentWidth + 10, 8, 'F');

        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(41, 128, 185);
        doc.text("CONDITIONS CONTRACTUELLES", margin, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 12;

        // Tableau des termes
        const terms = [];
        if (contrat.duree) terms.push(["Durée du contrat", `${contrat.duree} mois`]);
        if (contrat.fin_contrat) terms.push(["Date de fin", new Date(contrat.fin_contrat).toLocaleDateString("fr-FR")]);
        if (contrat.delai_preavis) terms.push(["Délai de préavis", `${contrat.delai_preavis} mois`]);
        if (contrat.date_preavis) terms.push(["Date limite de préavis", new Date(contrat.date_preavis).toLocaleDateString("fr-FR")]);
        if (contrat.periodicite_paiement) terms.push(["Périodicité de paiement", contrat.periodicite_paiement]);
        if (contrat.loyer_ht) terms.push(["Loyer HT ", `${contrat.loyer_ht} `]);
        if (contrat.taux_tva_loyer) terms.push(["Taux de TVA", `${contrat.taux_tva_loyer} %`]);
        if (contrat.etat) terms.push(["État du contrat", contrat.etat]);
        if (contrat.compte_comptable) terms.push(["Compte comptable", contrat.compte_comptable]);

        doc.setFontSize(9);
        const rowHeight = 8;
        const labelWidth = contentWidth * 0.5;

        terms.forEach(([key, value], index) => {
            checkPageBreak(rowHeight + 5);

            // Alternance de couleur pour les lignes
            if (index % 2 === 0) {
                doc.setFillColor(250, 250, 250);
                doc.rect(margin, yPos - 2, contentWidth, rowHeight, 'F');
            }

            doc.setFont("helvetica", "bold");
            doc.text(key, margin + 3, yPos + 4);
            doc.setFont("helvetica", "normal");
            doc.text(value, margin + labelWidth, yPos + 4);

            yPos += rowHeight;
        });

        // Bordure du tableau
        doc.setDrawColor(200, 200, 200);
        doc.rect(margin, yPos - (terms.length * rowHeight) - 2, contentWidth, terms.length * rowHeight);

        yPos += 15;
        checkPageBreak(60);

        // ==================== SIGNATURES ====================
        doc.setFillColor(245, 245, 245);
        doc.rect(margin - 5, yPos - 5, contentWidth + 10, 8, 'F');

        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(41, 128, 185);
        doc.text("SIGNATURES", margin, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 15;

        // Zones de signature côte à côte
        const signatureY = yPos;
        const signatureWidth = (contentWidth - 10) / 2;

        // Signature Fournisseur
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Le Fournisseur", margin, signatureY);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text("Date et signature :", margin, signatureY + 6);
        doc.setDrawColor(150, 150, 150);

        // Signature Acheteur
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("L'Acheteur", acheteurX, signatureY);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text("GROUPE SOBER SAS", acheteurX, signatureY + 6);
        doc.text("Date et signature :", acheteurX, signatureY + 12);

        // ==================== PIED DE PAGE ====================
        const footerY = pageHeight - 15;
        doc.setFontSize(7);
        doc.setTextColor(128, 128, 128);
        doc.setFont("helvetica", "italic");
        doc.text(
            " GROUPE SOBER SAS - 391 rue de l'Avenir, 69830 SAINT GEORGES DE RENEINS",
            pageWidth / 2,
            footerY,
            { align: "center" }
        );

        // Sauvegarde
        const fileName = `Contrat_${contrat.n_contact || 'sans_numero'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
    };

    const handleDownloadLetterPDF = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const rightMargin = pageWidth - 20;
        const leftMargin = 20;
        const footerMargin = pageHeight - 15;

        const footerText = [
            "Groupe SOBER SAS – siège social",
            "391 rue de l'Avenir - Z.I. des Vernailles Ouest - 69830 SAINT GEORGES DE RENEINS - France",
            "T : 04.74.60.75.03 - F : 04.74.60.71.06 – M : commercial@sober.fr – S : www.sober.fr",
            "S.A.S. au capital de 1 100 000 € - 822 951 026 RCS Villefranche-Sur-Saône",
        ];

        // Function to add footer to a page
        // const addFooter = () => {
        //     // Ligne de séparation au-dessus du pied de page
        //     doc.setDrawColor(135, 206, 250);
        //     doc.setLineWidth(0.5);
        //     doc.line(leftMargin, footerMargin - 5, pageWidth - 20, footerMargin - 5);

        //     doc.setFontSize(7);
        //     footerText.forEach((line, index) => {
        //         const lineWidth = doc.getTextWidth(line);
        //         const xPos = (pageWidth - lineWidth) / 2;
        //         if (index === 0) {
        //             doc.setFont("helvetica", "bold");
        //             doc.setTextColor(135, 206, 250);
        //         } else {
        //             doc.setFont("helvetica", "normal");
        //             doc.setTextColor(80, 80, 80);
        //         }
        //         doc.text(line, xPos, footerMargin + index * 3);
        //     });
        //     doc.setTextColor(0, 0, 0);
        //     doc.setFont("helvetica", "normal");
        //     doc.setFontSize(9);
        // };

        // Add logo — selon la société du contrat sélectionné
        const getLogoPath = () => {
            const societeNom = (selectedContrat?.societe_nom || "").toLowerCase().trim();
            if (societeNom.includes("soca medical")) return "/assets/SOCAMEDICAL.png";
            if (societeNom.includes("soca international")) return "/assets/SOCAINTERNATIONAL.png";
            if (societeNom.includes("mediprod")) return "/assets/MEDIPROD.png";
            if (societeNom.includes("medical digital")) return "/assets/MDA.png";
            if (societeNom.includes("groupe sober")) return "/assets/SOBER.png";
            if (societeNom.includes("FINAO Transition")) return "/assets/FINAO.png";
            return "/assets/SOBER.png";
        };
        try {
            doc.addImage(getLogoPath(), "PNG", 10, 10);
        } catch (error) {
            console.error("Error loading logo:", error);
        }

        // Add recipient details with improved formatting
        let yPos = 70;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(letterContent.recipientName, rightMargin, yPos, { align: "right" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        if (letterContent.recipientContact) {
            yPos += 5;
            doc.text(letterContent.recipientContact, rightMargin, yPos, { align: "right" });
        }
        if (letterContent.recipientAddress) {
            yPos += 5;
            doc.text(letterContent.recipientAddress, rightMargin, yPos, { align: "right" });
        }
        if (letterContent.recipientCP || letterContent.recipientCity) {
            yPos += 5;
            doc.text(`${letterContent.recipientCP || ""} ${letterContent.recipientCity || ""}`, rightMargin, yPos, { align: "right" });
        }
        if (letterContent.recipientCountry) {
            yPos += 5;
            doc.text(letterContent.recipientCountry, rightMargin, yPos, { align: "right" });
        }

        // Objet en gras et souligné
        yPos += 15;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        const objetText = `Objet : ${letterContent.subject}`;
        doc.text(objetText, leftMargin, yPos);

        // Souligner l'objet
        const objetWidth = doc.getTextWidth(objetText);
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.line(leftMargin, yPos + 1, leftMargin + objetWidth, yPos + 1);

        // Date
        yPos += 8;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.text(`St Georges de Reneins, le ${new Date().toLocaleDateString("fr-FR")}`, leftMargin, yPos);

        // Corps de la lettre avec meilleure lisibilité
        yPos += 15;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const maxWidth = 160;
        const bodyText = doc.splitTextToSize(letterContent.body, maxWidth);
        let currentPage = 1;

        bodyText.forEach((line) => {
            if (yPos > pageHeight - 38) {
                // addFooter();
                doc.addPage();
                currentPage++;
                yPos = 20;
            }
            doc.text(line, leftMargin, yPos);
            yPos += 5;
        });

        // Formule de politesse avec espacement
        yPos += 10;
        if (yPos > pageHeight - 50) {
            // addFooter();
            doc.addPage();
            currentPage++;
            yPos = 20;
        }

        // Encadré pour l'expéditeur et signature
        const senderBlockY = yPos;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(letterContent.sender, leftMargin, yPos);

        // Signature
        const signatureUrl = "/assets/signature.png";
        const signatureWidth = 40;
        const signatureHeight = 25;
        try {
            yPos += 8;

            if (yPos + signatureHeight + 15 > footerMargin - 8) {
                // addFooter();
                doc.addPage();
                currentPage++;
                yPos = 20;
            }

            doc.addImage(signatureUrl, "PNG", leftMargin, yPos, signatureWidth, signatureHeight);
            yPos += signatureHeight + 3;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text("Eric LATTIER", leftMargin, yPos);

            doc.setFont("helvetica", "italic");
            doc.setFontSize(9);
            doc.text("Président", leftMargin, yPos + 5);
        } catch (error) {
            console.error("Error loading signature:", error);
        }

        // Add footer to the last page
        // addFooter();

        // Numérotation des pages (seulement si plus d'une page)
        const pageCount = doc.internal.getNumberOfPages();
        if (pageCount > 1) {
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(7);
                doc.setTextColor(128, 128, 128);
                doc.setFont("helvetica", "italic");
                doc.text(`Page ${i}/${pageCount}`, pageWidth - 25, 10, { align: "right" });
            }
        }

        // Save the PDF 
        const date = new Date().toISOString().split('T')[0];
        doc.save(`Lettre_resiliation_${selectedContrat.n_contact}_${date}.pdf`);
        setOpenLetter(false);
    };



    const columns = [
        {
            field: "compte_comptable", headerName: "Compte Comptable", flex: 2,
            minWidth: 180, sortable: false,
        },
        { field: "n_contact", headerName: "N° Contrat", width: 150, sortable: false, },

        { field: "nom_fournisseur", headerName: "Nom Fournisseur", width: 160, sortable: false, },
        { field: "nom_contact", headerName: "Nom Contact", width: 160, sortable: false, },
        { field: "telephone_contact", headerName: "Téléphone", width: 150, sortable: false, },
        { field: "email_contact", headerName: "Email", width: 200, sortable: false, },
        { field: "pays_fournisseur", headerName: "Pays Fournisseur", width: 160, sortable: false, },

        {
            field: "etat",
            headerName: "État",
            width: 130,
            renderCell: (params) => (
                <Chip
                    label={params.value || "-"}
                    size="small"
                    sx={getChipStyles(theme)}
                />
            ),
            sortable: false,
        },
        {
            field: "date_signature",
            headerName: "Date Signature",
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "-"}
                </Typography>
            ),
            sortable: true,
        },
        { field: "duree", headerName: "Durée (mois)", width: 150, sortable: false, },
        {
            field: "fin_contrat",
            headerName: "Fin Contrat",
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "-"}
                </Typography>
            ),
            sortable: true,
        },
        {
            field: "date_preavis",
            headerName: "Date Préavis",
            width: 150,
            renderCell: (params) => {
                const d = params.row.date_preavis
                    || calculateDatePreavis(
                        params.row.fin_contrat
                            ? new Date(params.row.fin_contrat).toISOString().split("T")[0]
                            : "",
                        params.row.delai_preavis
                    );
                return (
                    <Typography variant="body2">
                        {d ? new Date(d).toLocaleDateString("fr-FR") : "-"}
                    </Typography>
                );
            },
            sortable: true,
        },
        {
            field: "periode_reengagement",
            headerName: "Période réengagement",
            width: 200,
            sortable: false,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value ? `${params.value} mois` : "-"}
                </Typography>
            ),
        },

        {
            field: "periodicite_paiement",
            headerName: "Périodicité Paiement",
            width: 180,
            renderCell: (params) => (
                <Chip
                    label={params.value || "-"}
                    size="small"
                    sx={getChipStyles(theme)}
                />
            ),
            sortable: false,
        },
        { field: "loyer_ht", headerName: "Loyer HT", width: 150, sortable: false, },
        { field: "devise", headerName: "Devise", width: 150, sortable: false, },
        { field: "taux_tva_loyer", headerName: "Taux TVA Loyer (%)", width: 150, sortable: false, },
        {
            field: "fichier_pdf",
            headerName: "Contrat PDF",
            flex: 2,
            minWidth: 150,
            renderCell: (params) =>
                params.row.fichier_pdf ? (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={() => handleViewPdf(params.row)}
                        sx={{
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            color: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                borderColor: theme.palette.primary.main,
                            },
                        }}
                    >
                        Télécharger
                    </Button>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        -
                    </Typography>
                ),
            sortable: false,
        },
        {
            field: "societe_nom",
            headerName: "Société",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <Typography variant="body2">{params.row.societe_nom || "-"}</Typography>
            ),
            sortable: false,
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 200,
            renderCell: (params) => (
                <Box display="flex" gap={0.5}>
                    <StyledIconButton
                        onClick={() => handleLetterClick(params.row)}
                        size="small"
                        title="Créer Lettre"
                    >
                        <ReceiptOutlinedIcon fontSize="small" />
                    </StyledIconButton>
                    <StyledIconButton
                        onClick={() => handleEditClick(params.row)}
                        size="small"
                        title="Modifier"
                    >
                        <Edit fontSize="small" />
                    </StyledIconButton>
                    <StyledIconButton
                        onClick={() => handleDeleteClick(params.row)}
                        size="small"
                        title="Supprimer"
                        sx={{ color: theme.palette.error.main }}
                    >
                        <Delete fontSize="small" />
                    </StyledIconButton>
                    <StyledIconButton
                        onClick={() => handleDownloadContractPDF(params.row)}
                        size="small"
                        title="Télécharger Contrat PDF"
                    >
                        <Download fontSize="small" />
                    </StyledIconButton>
                </Box>
            ),
            sortable: false,
        },
    ];

    const filteredContrats = useMemo(() => {
        return contrats.filter((c) => {
            const matchPeriodicite = filterPeriodicite
                ? c.periodicite_paiement === filterPeriodicite
                : true;

            const matchSociete = filterSociete
                ? c.societe_id === parseInt(filterSociete)
                : true;

            // filtre fournisseur
            const matchFournisseur = filterFromAlerte && alerteFilterNom
                ? c.nom_fournisseur?.toLowerCase().includes(alerteFilterNom.toLowerCase())
                : true;

            return matchPeriodicite && matchSociete && matchFournisseur;
        });
    }, [contrats, filterPeriodicite, filterSociete, filterFromAlerte, alerteFilterNom]);

    const [searchText, setSearchText] = useState("");

    const filteredBySearch = useMemo(() => {
        if (!searchText) return filteredContrats;

        const lowerSearch = searchText.toLowerCase();
        return filteredContrats.filter((row) =>
            Object.values(row).some((value) =>
                value != null && value.toString().toLowerCase().includes(lowerSearch)
            )
        );
    }, [filteredContrats, searchText]);




    return (
        <Box

            sx={{
                m: { xs: 1, sm: 2, md: 3 },
            }}
        >
            <Header
                title="CONTRATS"
                subtitle="Gestion centralisée des contrats fournisseurs"
            />
            <StyledCard>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, fontSize: { xs: "0.8rem", sm: "1rem" } }}>
                        {error}
                    </Alert>
                )}
                <Box
                    mb={2}
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    gap={{ xs: 1, sm: 2 }}
                >
                    {/* Barre de recherche + Filtres */}
                    <Box
                        display="flex"
                        flexDirection={{ xs: "column", sm: "row" }}
                        gap={{ xs: 1.5, sm: 2 }}
                        alignItems="stretch"
                        flex={1}
                        maxWidth={{ sm: 700 }}
                    >
                        <TextField
                            size="small"
                            placeholder="Rechercher dans tous les champs..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                            }}
                            sx={{
                                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                borderRadius: "8px",
                                flex: 1,
                                minWidth: 200,
                                '& .MuiOutlinedInput-root': {
                                    fontSize: { xs: '0.8rem', sm: '1rem' },
                                },
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="filter-periodicite-label">Filtrer par périodicité</InputLabel>
                            <Select
                                labelId="filter-periodicite-label"
                                value={filterPeriodicite}
                                label="Filtrer par périodicité"
                                onChange={(e) => setFilterPeriodicite(e.target.value)}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    borderRadius: "8px",
                                    "& .MuiSelect-select": { fontSize: { xs: "0.8rem", sm: "1rem" } },
                                }}
                            >
                                <MenuItem value="">Tous</MenuItem>
                                {periodicitesPaiement.map((periodicite) => (
                                    <MenuItem key={periodicite} value={periodicite}>
                                        {periodicite}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Toutes les sociétés</InputLabel>
                            <Select
                                value={filterSociete}
                                onChange={(e) => setFilterSociete(e.target.value)}
                                label="Toutes les sociétés"
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    borderRadius: "8px",
                                }}
                            >
                                <MenuItem value="">Toutes les sociétés</MenuItem>
                                {(userRole === "admin" ? allSocietes : uniqueSocietes).map((societe) => (
                                    <MenuItem key={societe.id} value={societe.id}>{societe.nom}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Boutons à droite */}
                    <Box
                        display="flex"
                        flexDirection={{ xs: "column", sm: "row" }}
                        gap={1}
                        width={{ xs: "100%", sm: "auto" }}
                    >
                        {(userRole === "gestionnaire" || userRole === "admin") && (
                            <Button
                                variant="contained"
                                onClick={() => setOpenAdd(true)}
                                sx={{
                                    borderRadius: '8px',
                                    background: `linear-gradient(135deg, #2a5fa9 0%, #4cbed8 100%)`,
                                    color: '#ffffff',
                                    '&:hover': {
                                        background: `linear-gradient(135deg, #4cbed8 0%, #65b986 100%)`,
                                        transform: 'scale(1.05)',
                                    },
                                    width: { xs: '100%', sm: 'auto' },
                                }}
                            >
                                Ajouter un contrat
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Download />}
                            onClick={handleDownloadExcel}
                            sx={{
                                borderRadius: '8px',
                                background: `linear-gradient(135deg, #2a5fa9 0%, #4cbed8 100%)`,
                                color: '#ffffff',
                                '&:hover': {
                                    background: `linear-gradient(135deg, #4cbed8 0%, #65b986 100%)`,
                                    transform: 'scale(1.05)',
                                },
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            Télécharger Excel
                        </Button>
                    </Box>
                </Box>

                <Menu
                    anchorEl={anchorElColumns}
                    open={Boolean(anchorElColumns)}
                    onClose={() => setAnchorElColumns(null)}
                    disableAutoFocusItem
                    keepMounted
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: { maxHeight: 500, width: 320, '& .MuiList-root': { p: 0 } },
                    }}
                >
                    <Box sx={{ px: 1, pt: 1 }}>
                        <TextField
                            fullWidth
                            placeholder="Rechercher une colonne"
                            size="small"
                            variant="outlined"
                            value={columnSearch}
                            onChange={(e) => setColumnSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            InputProps={{
                                startAdornment: <Search fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
                        />
                    </Box>

                    <Box sx={{ maxHeight: 320, overflowY: 'auto', px: 1, py: 1 }}>
                        {[
                            { field: "compte_comptable", headerName: "Compte Comptable" },
                            { field: "n_contact", headerName: "N° Contrat" },
                            { field: "nom_fournisseur", headerName: "Nom Fournisseur" },
                            { field: "nom_contact", headerName: "Nom Contact" },
                            { field: "telephone_contact", headerName: "Téléphone" },
                            { field: "email_contact", headerName: "Email" },
                            { field: "pays_fournisseur", headerName: "Pays Fournisseur" },
                            { field: "etat", headerName: "État" },
                            { field: "date_signature", headerName: "Date Signature" },
                            { field: "duree", headerName: "Durée (mois)" },
                            { field: "fin_contrat", headerName: "Fin Contrat" },
                            { field: "date_preavis", headerName: "Date Préavis" },
                            { field: "periode_reengagement", headerName: "Période réengagement" },
                            { field: "periodicite_paiement", headerName: "Périodicité Paiement" },
                            { field: "loyer_ht", headerName: "Loyer HT" },
                            { field: "devise", headerName: "Devise" },
                            { field: "taux_tva_loyer", headerName: "Taux TVA Loyer (%)" },
                            { field: "fichier_pdf", headerName: "Contrat PDF" },
                            { field: "societe_nom", headerName: "Société" },
                            { field: "actions", headerName: "Actions" },
                        ]
                            .filter(col => col.headerName.toLowerCase().includes(columnSearch.toLowerCase()))
                            .map((col) => (
                                <Box
                                    key={col.field}
                                    onClick={() => {
                                        setColumnVisibilityModel((prev) => ({
                                            ...prev,
                                            [col.field]: !(prev[col.field] ?? true),
                                        }));
                                    }}
                                    sx={{
                                        py: 0.75, px: 1.5, borderRadius: 1, mb: 0.25,
                                        display: 'flex', alignItems: 'center', cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        '&:hover': { backgroundColor: 'action.hover' },
                                    }}
                                >
                                    <Box sx={{
                                        width: 34, height: 20, borderRadius: '10px',
                                        backgroundColor: (columnVisibilityModel[col.field] ?? true) ? '#1976d2' : '#e0e0e0',
                                        position: 'relative', transition: 'background-color 0.2s',
                                        mr: 1.5, flexShrink: 0,
                                    }}>
                                        <Box sx={{
                                            width: 16, height: 16, borderRadius: '50%',
                                            backgroundColor: 'white', position: 'absolute', top: '2px',
                                            left: (columnVisibilityModel[col.field] ?? true) ? '16px' : '2px',
                                            transition: 'left 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.875rem', userSelect: 'none' }}>
                                        {col.headerName}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>

                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5 }}>
                        <Button
                            size="small"
                            onClick={() => {
                                const allHidden = {
                                    compte_comptable: false, n_contact: false, nom_fournisseur: false,
                                    nom_contact: false, telephone_contact: false, email_contact: false,
                                    pays_fournisseur: false, etat: false, date_signature: false,
                                    duree: false, fin_contrat: false, date_preavis: false,
                                    periode_reengagement: false, periodicite_paiement: false,
                                    loyer_ht: false, devise: false, taux_tva_loyer: false,
                                    fichier_pdf: false, societe_nom: false, actions: false,
                                };
                                setColumnVisibilityModel(allHidden);
                            }}
                            sx={{ textTransform: 'none', fontSize: '0.813rem' }}
                        >
                            MASQUER TOUT
                        </Button>
                        <Button
                            size="small"
                            onClick={() => setColumnVisibilityModel({})}
                            sx={{ textTransform: 'none', fontSize: '0.813rem' }}
                        >
                            AFFICHER TOUT
                        </Button>
                    </Box>
                </Menu>

                <Menu
                    anchorEl={anchorElDensity}
                    open={Boolean(anchorElDensity)}
                    onClose={() => setAnchorElDensity(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <MenuItem onClick={() => { setDensity('compact'); setAnchorElDensity(null); }}>Compact</MenuItem>
                    <MenuItem onClick={() => { setDensity('standard'); setAnchorElDensity(null); }}>Standard</MenuItem>
                    <MenuItem onClick={() => { setDensity('comfortable'); setAnchorElDensity(null); }}>Confortable</MenuItem>
                </Menu>

                {filterFromAlerte && alerteFilterNom && (
                    <Alert
                        severity="info"
                        icon={<Search />}
                        sx={{ mb: 2, borderRadius: "12px" }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    setFilterFromAlerte(false);
                                    setAlerteFilterNom("");
                                    setHighlightId(null);
                                }}
                            >
                                Afficher tous les contrats
                            </Button>
                        }
                    >
                        Affichage filtré pour le fournisseur : <strong>{alerteFilterNom}</strong>
                    </Alert>
                )}

                <Box sx={{ height: "70vh", width: '100%', mt: 2, minHeight: 600, overflow: "hidden" }}>
                    <StyledDataGrid
                        rows={filteredBySearch}
                        columns={columns}
                        getRowId={(row) => row.id}
                        pagination
                        paginationMode="client"
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 20, page: 0 },
                            },
                        }}
                        pageSizeOptions={[10, 20, 50, 100]}
                        loading={isLoading}
                        disableSelectionOnClick
                        autoHeight={false}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                        disableColumnSorting={true}
                        disableColumnMenu={true}
                        density={density}
                        getRowClassName={(params) =>
                            params.id === highlightId ? "highlighted-row" : ""
                        }
                        components={{
                            Toolbar: useCallback(() => (
                                <CustomToolbar
                                    setAnchorElColumns={setAnchorElColumns}
                                    setAnchorElDensity={setAnchorElDensity}
                                />
                            ), []),
                        }}
                        localeText={{
                            toolbarColumns: "Colonnes",
                            toolbarDensity: "Densité",
                            toolbarExport: "Exporter",
                            columnsPanelShowAllButton: "Afficher tout",
                            columnsPanelHideAllButton: "Masquer tout",
                            noRowsLabel: "Aucun contrat",
                        }}
                        sx={{
                            width: "100% !important",
                            height: "100% !important",
                            "& .MuiDataGrid-columnHeader": {
                                "& .MuiDataGrid-columnSeparator": { visibility: "visible" }
                            },
                            "& .MuiDataGrid-virtualScroller": {
                                minHeight: "300px",
                            },
                            "& .highlighted-row": {
                                backgroundColor: `${alpha("#f59e0b", 0.25)} !important`,
                                outline: `2px solid #f59e0b`,
                                outlineOffset: "-2px",
                                transition: "background-color 0.5s ease",
                            },
                            "& .MuiDataGrid-sortIcon": { display: "none !important" },
                            "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon": { display: "none !important" },
                            "& .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-sortIcon": { display: "none !important" },
                            [theme.breakpoints.down("sm")]: { fontSize: "0.75rem" },
                        }}
                    />
                </Box>
            </StyledCard>

            {/* Add Contrat Dialog */}
            <Dialog
                open={openAdd}
                onClose={() => {
                    setOpenAdd(false);
                    setFormErrors({});
                    setFirstErrorField("");
                    setNewFichierPdf(null); // ── NOUVEAU ──
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(
                            theme.palette.background.paper,
                            0.95
                        )} 100%)`,
                        backdropFilter: "blur(20px)",
                        borderRadius: "20px",
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                    },
                }}
            >
                <DialogTitle sx={{ pb: 2 }}>
                    <SectionTitle>Ajouter un Contrat</SectionTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Remplissez les informations du nouveau contrat
                    </Typography>
                </DialogTitle>

                <Divider sx={{ mb: 2 }} />

                <DialogContent sx={{ pt: 2 }}>
                    {/* Informations Générales */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            Informations Générales
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                id="n_contact"
                                label="N° Contrat "
                                fullWidth
                                value={newContrat.n_contact}
                                onChange={(e) => {
                                    setNewContrat({ ...newContrat, n_contact: e.target.value });
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />

                            {/* Société — admin toujours, gestionnaire si plusieurs sociétés */}
                            {(userRole === "admin" || (userRole === "gestionnaire" && uniqueSocietes.length > 1)) && (
                                <FormControl fullWidth error={!!formErrors.societe_id}>
                                    <InputLabel id="societe-add-label">Société *</InputLabel>
                                    <Select
                                        labelId="societe-add-label"
                                        value={newContrat.societe_id || ""}
                                        onChange={(e) => {
                                            setNewContrat({ ...newContrat, societe_id: e.target.value, compte_comptable_id: "" });
                                            if (formErrors.societe_id) setFormErrors({ ...formErrors, societe_id: "" });
                                        }}
                                        label="Société *"
                                        sx={{
                                            backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                            borderRadius: "12px",
                                        }}
                                    >
                                        <MenuItem value=""><em>Sélectionner une société</em></MenuItem>
                                        {(userRole === "admin" ? allSocietes : uniqueSocietes).map((societe) => (
                                            <MenuItem key={societe.id} value={societe.id}>{societe.nom}</MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>{formErrors.societe_id || " "}</FormHelperText>
                                </FormControl>
                            )}

                            {/* Si gestionnaire avec une seule société — auto-sélection silencieuse */}
                            {userRole === "gestionnaire" && uniqueSocietes.length === 1 && (
                                // Auto-set via useEffect ci-dessous
                                null
                            )}
                            <TextField
                                id="nom_fournisseur"
                                label="Nom Fournisseur *"
                                fullWidth
                                value={newContrat.nom_fournisseur}
                                onChange={(e) => {
                                    setNewContrat({ ...newContrat, nom_fournisseur: e.target.value });
                                    if (formErrors.nom_fournisseur) {
                                        setFormErrors({ ...formErrors, nom_fournisseur: "" });
                                    }
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                id="code_fournisseur"
                                label="Code Fournisseur"
                                fullWidth
                                value={newContrat.code_fournisseur}
                                onChange={(e) => setNewContrat({ ...newContrat, code_fournisseur: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <Box id="compte_comptable_id">
                                {!customCompteComptableAdd ? (
                                    <FormControl
                                        fullWidth
                                        disabled={!newContrat.societe_id || loadingComptes}
                                        error={!!formErrors.compte_comptable_id}
                                    >
                                        <InputLabel id="compte-comptable-add-label">Compte Comptable *</InputLabel>
                                        <Select
                                            labelId="compte-comptable-add-label"
                                            value={newContrat.compte_comptable_id || ""}
                                            onChange={(e) => {
                                                setNewContrat({ ...newContrat, compte_comptable_id: e.target.value });
                                                if (formErrors.compte_comptable_id) setFormErrors({ ...formErrors, compte_comptable_id: "" });
                                            }}
                                            label="Compte Comptable *"
                                            sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "12px" }}
                                        >
                                            <MenuItem value="" disabled>
                                                {loadingComptes ? "Chargement..." : "Choisir un compte"}
                                            </MenuItem>
                                            {comptesComptables.map((c) => (
                                                <MenuItem key={c.id} value={c.id}>{c.code} — {c.libelle}</MenuItem>
                                            ))}
                                            <Divider sx={{ my: 0.5 }} />
                                            <MenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCustomCompteComptableAdd(true);
                                                    setNewContrat({ ...newContrat, compte_comptable_id: "" });
                                                }}
                                                sx={{ mx: 0.5, borderRadius: "8px", color: "#2a5fa9", fontWeight: 600, fontSize: "0.875rem" }}
                                            >
                                                + Saisir manuellement
                                            </MenuItem>
                                        </Select>
                                        <FormHelperText>
                                            {formErrors.compte_comptable_id ||
                                                (!newContrat.societe_id ? "Sélectionnez d'abord une société." : " ")}
                                        </FormHelperText>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        label="Compte Comptable (saisie manuelle) *"
                                        fullWidth
                                        autoFocus
                                        value={newContrat.compte_comptable_manuel || ""}
                                        onChange={(e) => {
                                            setNewContrat({ ...newContrat, compte_comptable_manuel: e.target.value });
                                            if (formErrors.compte_comptable_id) setFormErrors({ ...formErrors, compte_comptable_id: "" });
                                        }}
                                        error={!!formErrors.compte_comptable_id}
                                        helperText={formErrors.compte_comptable_id || " "}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setCustomCompteComptableAdd(false);
                                                        setNewContrat({ ...newContrat, compte_comptable_manuel: "" });
                                                    }}
                                                    sx={{ color: "error.main", minWidth: "auto" }}
                                                >
                                                    ✕
                                                </Button>
                                            ),
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Contact Fournisseur */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            Contact Fournisseur
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                id="nom_contact"
                                label="Nom Contact *"
                                fullWidth
                                value={newContrat.nom_contact}
                                onChange={(e) => {
                                    setNewContrat({ ...newContrat, nom_contact: e.target.value });
                                    if (formErrors.nom_contact) {
                                        setFormErrors({ ...formErrors, nom_contact: "" });
                                    }
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                id="telephone_contact"
                                label="Téléphone Contact"
                                fullWidth
                                value={newContrat.telephone_contact}
                                onChange={(e) => setNewContrat({ ...newContrat, telephone_contact: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />

                            <TextField
                                id="email_contact"
                                label="Email Contact"
                                type="email"
                                fullWidth
                                value={newContrat.email_contact}
                                onChange={(e) => setNewContrat({ ...newContrat, email_contact: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />

                            <TextField
                                id="adresse_fournisseur"
                                label="Adresse Fournisseur *"
                                fullWidth
                                value={newContrat.adresse_fournisseur}
                                onChange={(e) => setNewContrat({ ...newContrat, adresse_fournisseur: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    id="cp_fournisseur"
                                    label="CP *"
                                    fullWidth
                                    value={newContrat.cp_fournisseur}
                                    onChange={(e) => setNewContrat({ ...newContrat, cp_fournisseur: e.target.value })}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                    }}
                                />
                                <TextField
                                    id="ville_fournisseur"
                                    label="Ville *"
                                    fullWidth
                                    value={newContrat.ville_fournisseur}
                                    onChange={(e) => setNewContrat({ ...newContrat, ville_fournisseur: e.target.value })}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                    }}
                                />
                            </Box>
                            <TextField
                                id="pays_fournisseur"
                                label="Pays *"
                                fullWidth
                                value={newContrat.pays_fournisseur}
                                onChange={(e) => setNewContrat({ ...newContrat, pays_fournisseur: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Durée et Échéances */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            Durée et Échéances
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                id="date_signature"
                                label="Date Signature *"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={newContrat.date_signature}
                                onChange={(e) => {
                                    const fin = calculateFinContrat(e.target.value, newContrat.duree);
                                    const preavis = calculateDatePreavis(fin, newContrat.delai_preavis);
                                    setNewContrat({ ...newContrat, date_signature: e.target.value, fin_contrat: fin, date_preavis: preavis });
                                    if (formErrors.date_signature) {
                                        setFormErrors({ ...formErrors, date_signature: "" });
                                    }
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                id="duree"
                                label="Durée (mois) *"
                                type="number"
                                fullWidth
                                value={newContrat.duree}
                                onChange={(e) => {
                                    const fin = calculateFinContrat(newContrat.date_signature, e.target.value);
                                    const preavis = calculateDatePreavis(fin, newContrat.delai_preavis);
                                    setNewContrat({ ...newContrat, duree: e.target.value, fin_contrat: fin, date_preavis: preavis });
                                    if (formErrors.duree) {
                                        setFormErrors({ ...formErrors, duree: "" });
                                    }
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Fin Contrat"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={newContrat.fin_contrat}
                                onChange={(e) => {
                                    const preavis = calculateDatePreavis(e.target.value, newContrat.delai_preavis);
                                    setNewContrat({ ...newContrat, fin_contrat: e.target.value, date_preavis: preavis });
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                id="delai_preavis"
                                label="Délai Préavis (mois) *"
                                type="number"
                                fullWidth
                                value={newContrat.delai_preavis}
                                onChange={(e) => {
                                    const preavis = calculateDatePreavis(newContrat.fin_contrat, e.target.value);
                                    setNewContrat({ ...newContrat, delai_preavis: e.target.value, date_preavis: preavis });
                                    if (formErrors.delai_preavis) {
                                        setFormErrors({ ...formErrors, delai_preavis: "" });
                                    }
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Date Préavis"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={newContrat.date_preavis}
                                InputProps={{ readOnly: true }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                id="periode_reengagement"
                                label="Période de réengagement (mois)"
                                type="number"
                                fullWidth
                                value={newContrat.periode_reengagement}
                                onChange={(e) => {
                                    setNewContrat({ ...newContrat, periode_reengagement: e.target.value });
                                    if (formErrors.periode_reengagement) {
                                        setFormErrors({ ...formErrors, periode_reengagement: "" });
                                    }
                                }}
                                InputProps={{ inputProps: { min: 0 } }}
                                error={!!formErrors.periode_reengagement}
                                helperText={formErrors.periode_reengagement || " "}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Conditions Financières */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            Conditions Financières
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                id="loyer_ht"
                                label="Loyer HT *"
                                fullWidth
                                value={newContrat.loyer_ht}
                                onChange={(e) => {
                                    setNewContrat({ ...newContrat, loyer_ht: e.target.value });
                                    if (formErrors.loyer_ht) {
                                        setFormErrors({ ...formErrors, loyer_ht: "" });
                                    }
                                }}
                                error={!!formErrors.loyer_ht}
                                InputProps={{ inputProps: { min: 0 } }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                id="devise"
                                label="Devise"
                                fullWidth
                                value={newContrat.devise}
                                onChange={(e) => setNewContrat({ ...newContrat, devise: e.target.value })}
                                placeholder="ex: EUR, DZD, USD..."
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />

                            <FormControl fullWidth error={!!formErrors.taux_tva_loyer}>

                                <InputLabel id="taux-tva-loyer-label">Taux TVA Loyer (%) *</InputLabel>
                                <Select
                                    id="taux_tva_loyer"
                                    labelId="taux-tva-loyer-label"
                                    value={newContrat.taux_tva_loyer || ""}
                                    onChange={(e) => {
                                        setNewContrat({ ...newContrat, taux_tva_loyer: e.target.value });
                                        if (formErrors.taux_tva_loyer) {
                                            setFormErrors({ ...formErrors, taux_tva_loyer: "" });
                                        }
                                    }}
                                    label="Taux TVA Loyer (%) *"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        borderRadius: "12px",
                                    }}
                                >
                                    {tauxTVAOptions.map((taux) => (
                                        <MenuItem key={taux} value={taux}>{taux}%</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>{formErrors.taux_tva_loyer || " "}</FormHelperText>
                            </FormControl>
                            <FormControl fullWidth error={!!formErrors.periodicite_paiement}>
                                <InputLabel id="periodicite-paiement-label">Périodicité Paiement </InputLabel>
                                <Select
                                    id="periodicite_paiement"
                                    labelId="periodicite-paiement-label"
                                    value={newContrat.periodicite_paiement}
                                    onChange={(e) => {
                                        setNewContrat({ ...newContrat, periodicite_paiement: e.target.value });

                                    }}
                                    label="Périodicité Paiement *"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        borderRadius: "12px",
                                    }}
                                >
                                    {periodicitesPaiement.map((p) => (
                                        <MenuItem key={p} value={p}>{p}</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>{formErrors.periodicite_paiement || " "}</FormHelperText>
                            </FormControl>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Statut et Reconduction */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            Statut et Reconduction*
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <FormControl fullWidth error={!!formErrors.etat}>
                                <InputLabel id="etat-contrat-label">État du Contrat *</InputLabel>
                                <Select
                                    id="etat"
                                    labelId="etat-contrat-label"
                                    value={newContrat.etat}
                                    onChange={(e) => {
                                        setNewContrat({ ...newContrat, etat: e.target.value });
                                        if (formErrors.etat) {
                                            setFormErrors({ ...formErrors, etat: "" });
                                        }
                                    }}
                                    label="État du Contrat *"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        borderRadius: "12px",
                                    }}
                                >
                                    {etatsDisponibles.map((etat) => (
                                        <MenuItem key={etat} value={etat}>{etat}</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>{formErrors.etat || " "}</FormHelperText>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="reconduction-tacite-label">Reconduction Tacite*</InputLabel>
                                <Select
                                    id="reconduction_tacite"
                                    labelId="reconduction-tacite-label"
                                    value={newContrat.reconduction_tacite || "Non"}
                                    onChange={(e) => setNewContrat({ ...newContrat, reconduction_tacite: e.target.value })}
                                    label="Reconduction Tacite"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        borderRadius: "12px",
                                    }}
                                >
                                    <MenuItem value="oui">Oui</MenuItem>
                                    <MenuItem value="non">Non</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Détails Complémentaires */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            Détails Complémentaires
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                id="details"
                                label="Détails / Objets du Contrat"
                                fullWidth
                                multiline
                                rows={4}
                                value={newContrat.details}
                                onChange={(e) => setNewContrat({ ...newContrat, details: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* ── NOUVEAU : Upload Fichier PDF ── */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            Fichier Contrat (PDF)
                        </Typography>
                        <Box
                            sx={{
                                border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                                borderRadius: "12px",
                                p: 3,
                                textAlign: "center",
                                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                                },
                            }}
                            onClick={() => document.getElementById("fichier-pdf-add").click()}
                        >
                            <input
                                id="fichier-pdf-add"
                                type="file"
                                accept="application/pdf"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) setNewFichierPdf(file);
                                }}
                            />
                            <Download sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.5), mb: 1 }} />
                            {newFichierPdf ? (
                                <Box>
                                    <Typography variant="body2" color="primary" fontWeight={600}>
                                        {newFichierPdf.name}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {(newFichierPdf.size / 1024 / 1024).toFixed(2)} Mo
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="textSecondary">
                                        Cliquez pour sélectionner un fichier PDF
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Formats acceptés : PDF uniquement (jusqu'à 50 Mo)
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {newFichierPdf && (
                            <Button
                                size="small"
                                color="error"
                                onClick={() => {
                                    setNewFichierPdf(null);
                                    document.getElementById("fichier-pdf-add").value = "";
                                }}
                                sx={{ mt: 1 }}
                            >
                                Supprimer le fichier
                            </Button>
                        )}
                    </Box>
                    {/* Barre de progression upload — visible uniquement pendant l'upload */}
                    {isUploading && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography variant="caption" color="primary" fontWeight={600}>
                                    Upload en cours...
                                </Typography>
                                <Typography variant="caption" color="primary" fontWeight={600}>
                                    {uploadProgress}%
                                </Typography>
                            </Box>
                            <Box sx={{
                                width: "100%",
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                overflow: "hidden",
                            }}>
                                <Box sx={{
                                    height: "100%",
                                    width: `${uploadProgress}%`,
                                    borderRadius: 4,
                                    background: "linear-gradient(90deg, #2a5fa9 0%, #3a7fb5 20%, #4a9fc0 40%, #5ab8a0 60%, #70bd8d 80%, #8fc68b 100%)",
                                    transition: "width 0.3s ease",
                                }} />
                            </Box>
                            {uploadProgress === 100 && (
                                <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                                    Traitement en cours...
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <Divider sx={{ mt: 2 }} />

                <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
                    <Button
                        onClick={() => {
                            setOpenAdd(false);
                            setNewContrat({
                                n_contact: "", nom_fournisseur: "", code_fournisseur: "", nom_contact: "",
                                adresse_fournisseur: "", cp_fournisseur: "", ville_fournisseur: "", pays_fournisseur: "",
                                compte_comptable: "", etat: "", date_signature: "", duree: "", fin_contrat: "",
                                delai_preavis: "", date_preavis: "", periodicite_paiement: "", loyer_ht: "",
                                taux_tva_loyer: "", details: "", reconduction_tacite: "Non",
                            });
                            setNewFichierPdf(null); // ── NOUVEAU ──
                            setFormErrors({});
                            setFirstErrorField("");
                        }}
                        sx={{
                            color: theme.palette.error.main,
                            borderRadius: "10px",
                            px: 3,
                            py: 1,
                            fontWeight: 500,
                            "&:hover": { backgroundColor: alpha(theme.palette.error.main, 0.1) },
                        }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleAddSubmit}
                        disabled={isUploading}
                        variant="contained"
                        color="primary"
                        sx={{
                            borderRadius: "10px",
                            px: 4,
                            py: 1,
                            fontWeight: 600,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                            "&:hover": {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                transform: "translateY(-2px)",
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                            },
                            transition: "all 0.3s ease",
                        }}
                    >
                        {isUploading ? "Upload..." : "Ajouter"}

                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Contrat Dialog */}
            <Dialog
                open={openEdit}
                onClose={() => {
                    setOpenEdit(false);
                    setEditFichierPdf(null); // ── NOUVEAU ──
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(
                            theme.palette.background.paper,
                            0.95
                        )} 100%)`,
                        backdropFilter: "blur(20px)",
                        borderRadius: "20px",
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                    },
                }}
            >
                <DialogTitle sx={{ pb: 2 }}>
                    <SectionTitle>Modifier le Contrat</SectionTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Modifiez les informations du contrat sélectionné
                    </Typography>
                </DialogTitle>

                <Divider sx={{ mb: 2 }} />

                <DialogContent sx={{ pt: 2 }}>
                    {/* Section: Informations Générales */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>Informations Générales</span>
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                label="N° Contrat *"
                                fullWidth
                                value={selectedContrat?.n_contact || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, n_contact: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />

                            {(userRole === "admin" || (userRole === "gestionnaire" && uniqueSocietes.length > 1)) && (
                                <FormControl fullWidth error={!!formErrors.societe_id}>
                                    <InputLabel id="societe-edit-label">Société *</InputLabel>
                                    <Select
                                        labelId="societe-edit-label"
                                        value={selectedContrat?.societe_id || ""}
                                        onChange={(e) => setSelectedContrat({ ...selectedContrat, societe_id: e.target.value, compte_comptable_id: "" })} label="Société *"
                                        sx={{
                                            backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                            borderRadius: "12px",
                                        }}
                                    >
                                        <MenuItem value=""><em>Sélectionner une société</em></MenuItem>
                                        {(userRole === "admin" ? allSocietes : uniqueSocietes).map((societe) => (
                                            <MenuItem key={societe.id} value={societe.id}>{societe.nom}</MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>{formErrors.societe_id || " "}</FormHelperText>
                                </FormControl>
                            )}
                            <TextField
                                label="Nom Fournisseur *"
                                fullWidth
                                value={selectedContrat?.nom_fournisseur || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, nom_fournisseur: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Code Fournisseur"
                                fullWidth
                                value={selectedContrat?.code_fournisseur || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, code_fournisseur: e.target.value })}

                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <Box>
                                {!customCompteComptableEdit ? (
                                    <FormControl
                                        fullWidth
                                        disabled={!selectedContrat?.societe_id || loadingComptesEdit}
                                        error={!!formErrors.compte_comptable_id}
                                    >
                                        <InputLabel id="compte-comptable-edit-label">Compte Comptable *</InputLabel>
                                        <Select
                                            labelId="compte-comptable-edit-label"
                                            value={selectedContrat?.compte_comptable_id || ""}
                                            onChange={(e) => {
                                                setSelectedContrat({ ...selectedContrat, compte_comptable_id: e.target.value });
                                                if (formErrors.compte_comptable_id) setFormErrors({ ...formErrors, compte_comptable_id: "" });
                                            }}
                                            label="Compte Comptable *"
                                            sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "12px" }}
                                        >
                                            <MenuItem value="" disabled>
                                                {loadingComptesEdit ? "Chargement..." : "Choisir un compte"}
                                            </MenuItem>
                                            {comptesComptablesEdit.map((c) => (
                                                <MenuItem key={c.id} value={c.id}>{c.code} — {c.libelle}</MenuItem>
                                            ))}
                                            <Divider sx={{ my: 0.5 }} />
                                            <MenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCustomCompteComptableEdit(true);
                                                    setSelectedContrat({ ...selectedContrat, compte_comptable_id: "" });
                                                }}
                                                sx={{ mx: 0.5, borderRadius: "8px", color: "#2a5fa9", fontWeight: 600, fontSize: "0.875rem" }}
                                            >
                                                + Saisir manuellement
                                            </MenuItem>
                                        </Select>
                                        <FormHelperText>
                                            {formErrors.compte_comptable_id ||
                                                (!selectedContrat?.societe_id ? "Sélectionnez d'abord une société." : " ")}
                                        </FormHelperText>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        label="Compte Comptable (saisie manuelle) *"
                                        fullWidth
                                        autoFocus
                                        value={selectedContrat?.compte_comptable_manuel || ""}
                                        onChange={(e) => {
                                            setSelectedContrat({ ...selectedContrat, compte_comptable_manuel: e.target.value });
                                            if (formErrors.compte_comptable_id) setFormErrors({ ...formErrors, compte_comptable_id: "" });
                                        }}
                                        error={!!formErrors.compte_comptable_id}
                                        helperText={formErrors.compte_comptable_id || " "}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setCustomCompteComptableEdit(false);
                                                        setSelectedContrat({ ...selectedContrat, compte_comptable_manuel: "" });
                                                    }}
                                                    sx={{ color: "error.main", minWidth: "auto" }}
                                                >
                                                    ✕
                                                </Button>
                                            ),
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Section: Contact Fournisseur */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>Contact Fournisseur</span>
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                label="Nom Contact"
                                fullWidth
                                value={selectedContrat?.nom_contact || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, nom_contact: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Téléphone Contact"
                                fullWidth
                                value={selectedContrat?.telephone_contact || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, telephone_contact: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />

                            <TextField
                                label="Email Contact"
                                type="email"
                                fullWidth
                                value={selectedContrat?.email_contact || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, email_contact: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Adresse Fournisseur"
                                fullWidth
                                value={selectedContrat?.adresse_fournisseur || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, adresse_fournisseur: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="CP"
                                    fullWidth
                                    value={selectedContrat?.cp_fournisseur || ""}
                                    onChange={(e) => setSelectedContrat({ ...selectedContrat, cp_fournisseur: e.target.value })}
                                    error={!!formErrors.cp_fournisseur}
                                    helperText={formErrors.cp_fournisseur || " "}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                    }}
                                />
                                <TextField
                                    label="Ville"
                                    fullWidth
                                    value={selectedContrat?.ville_fournisseur || ""}
                                    onChange={(e) => setSelectedContrat({ ...selectedContrat, ville_fournisseur: e.target.value })}
                                    error={!!formErrors.ville_fournisseur}
                                    helperText={formErrors.ville_fournisseur || " "}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                    }}
                                />
                            </Box>
                            <TextField
                                label="Pays"
                                fullWidth
                                value={selectedContrat?.pays_fournisseur || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, pays_fournisseur: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Section: Durée et Échéances */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>Durée et Échéances</span>
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                label="Date Signature *"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={selectedContrat?.date_signature || ""}
                                onChange={(e) => {
                                    const fin = calculateFinContrat(e.target.value, selectedContrat?.duree || "");
                                    const preavis = calculateDatePreavis(fin, selectedContrat?.delai_preavis || "");
                                    setSelectedContrat({
                                        ...selectedContrat,
                                        date_signature: e.target.value,
                                        fin_contrat: fin,
                                        date_preavis: preavis,
                                    });
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Durée (mois)"
                                type="number"
                                fullWidth
                                value={selectedContrat?.duree || ""}
                                onChange={(e) => {
                                    const fin = calculateFinContrat(selectedContrat?.date_signature || "", e.target.value);
                                    const preavis = calculateDatePreavis(fin, selectedContrat?.delai_preavis || "");
                                    setSelectedContrat({
                                        ...selectedContrat,
                                        duree: e.target.value,
                                        fin_contrat: fin,
                                        date_preavis: preavis,
                                    });
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Fin Contrat"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={selectedContrat?.fin_contrat || ""}
                                onChange={(e) => {
                                    const preavis = calculateDatePreavis(e.target.value, selectedContrat?.delai_preavis || "");
                                    setSelectedContrat({
                                        ...selectedContrat,
                                        fin_contrat: e.target.value,
                                        date_preavis: preavis,
                                    });
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Délai Préavis (mois)"
                                type="number"
                                fullWidth
                                value={selectedContrat?.delai_preavis || ""}
                                onChange={(e) => {
                                    const preavis = calculateDatePreavis(selectedContrat?.fin_contrat || "", e.target.value);
                                    setSelectedContrat({
                                        ...selectedContrat,
                                        delai_preavis: e.target.value,
                                        date_preavis: preavis,
                                    });
                                }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Date Préavis"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={selectedContrat?.date_preavis || ""}
                                InputProps={{ readOnly: true }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Période de réengagement (mois)"
                                type="number"
                                fullWidth
                                value={selectedContrat?.periode_reengagement || ""}
                                onChange={(e) => {
                                    setSelectedContrat({
                                        ...selectedContrat,
                                        periode_reengagement: e.target.value,
                                    });
                                }}
                                InputProps={{ inputProps: { min: 0 } }}
                                error={!!formErrors.periode_reengagement}
                                helperText={formErrors.periode_reengagement || " "}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Section: Conditions Financières */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>Conditions Financières</span>
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                label="Loyer HT *"
                                fullWidth
                                value={selectedContrat?.loyer_ht || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, loyer_ht: e.target.value })}
                                InputProps={{ inputProps: { min: 0 } }}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                            <TextField
                                label="Devise"
                                fullWidth
                                value={selectedContrat?.devise || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, devise: e.target.value })}
                                placeholder="ex: EUR, DZD, USD..."
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />

                            <FormControl fullWidth>
                                <InputLabel id="taux-tva-edit-label">Taux TVA Loyer (%)</InputLabel>
                                <Select
                                    labelId="taux-tva-edit-label"
                                    value={selectedContrat?.taux_tva_loyer || ""}
                                    onChange={(e) => setSelectedContrat({ ...selectedContrat, taux_tva_loyer: e.target.value })}
                                    error={!!formErrors.taux_tva_loyer}
                                    label="Taux TVA Loyer (%)"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        borderRadius: "12px",
                                    }}
                                >
                                    {tauxTVAOptions.map((taux) => (
                                        <MenuItem key={taux} value={taux}>{taux}%</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="periodicite-edit-label">Périodicité Paiement</InputLabel>
                                <Select
                                    labelId="periodicite-edit-label"
                                    value={selectedContrat?.periodicite_paiement || ""}
                                    onChange={(e) => setSelectedContrat({ ...selectedContrat, periodicite_paiement: e.target.value })}
                                    label="Périodicité Paiement"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        borderRadius: "12px",
                                    }}
                                >
                                    {periodicitesPaiement.map((p) => (
                                        <MenuItem key={p} value={p}>{p}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Section: Statut et Reconduction */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>Statut et Reconduction</span>
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <FormControl fullWidth>
                                <InputLabel id="etat-edit-label">État du Contrat</InputLabel>
                                <Select
                                    labelId="etat-edit-label"
                                    value={selectedContrat?.etat || ""}
                                    onChange={(e) => setSelectedContrat({ ...selectedContrat, etat: e.target.value })}
                                    label="État du Contrat"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        borderRadius: "12px",
                                    }}
                                >
                                    <MenuItem value=""><em>Aucun</em></MenuItem>
                                    {etatsDisponibles.map((etat) => (
                                        <MenuItem key={etat} value={etat}>{etat}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="reconduction-edit-label">Reconduction Tacite</InputLabel>
                                <Select
                                    labelId="reconduction-edit-label"
                                    value={selectedContrat?.reconduction_tacite || "Non"}
                                    onChange={(e) => setSelectedContrat({ ...selectedContrat, reconduction_tacite: e.target.value })}
                                    error={!!formErrors.reconduction_tacite}
                                    helperText={formErrors.reconduction_tacite || " "}
                                    label="Reconduction Tacite"
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                        borderRadius: "12px",
                                    }}
                                >
                                    <MenuItem value="oui">Oui</MenuItem>
                                    <MenuItem value="non">Non</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Section: Détails Complémentaires */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>Détails Complémentaires</span>
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                label="Détails / Objets du Contrat"
                                fullWidth
                                multiline
                                rows={4}
                                value={selectedContrat?.details || ""}
                                onChange={(e) => setSelectedContrat({ ...selectedContrat, details: e.target.value })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                                }}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>Fichier Contrat (PDF)</span>
                        </Typography>

                        {/* Afficher le fichier existant */}
                        {selectedContrat?.fichier_pdf && !editFichierPdf && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    p: 2,
                                    mb: 2,
                                    borderRadius: "12px",
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                    backgroundColor: alpha(theme.palette.success.main, 0.05),
                                }}
                            >
                                <Download sx={{ color: theme.palette.success.main }} />
                                <Box flex={1}>
                                    <Typography variant="body2" fontWeight={600} color="success.main">
                                        Fichier PDF existant
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {selectedContrat.fichier_pdf.split("/").pop()}
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    onClick={() =>
                                        window.open(
                                            `${process.env.REACT_APP_API_URL}/storage/contrat/${selectedContrat.fichier_pdf.split("/").pop()}`,
                                            "_blank"
                                        )
                                    }
                                >
                                    Voir
                                </Button>
                            </Box>
                        )}

                        <Box
                            sx={{
                                border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                                borderRadius: "12px",
                                p: 3,
                                textAlign: "center",
                                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                                },
                            }}
                            onClick={() => document.getElementById("fichier-pdf-edit").click()}
                        >
                            <input
                                id="fichier-pdf-edit"
                                type="file"
                                accept="application/pdf"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) setEditFichierPdf(file);
                                }}
                            />
                            <Download sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.5), mb: 1 }} />
                            {editFichierPdf ? (
                                <Box>
                                    <Typography variant="body2" color="primary" fontWeight={600}>
                                        {editFichierPdf.name}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {(editFichierPdf.size / 1024 / 1024).toFixed(2)} Mo
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="textSecondary">
                                        {selectedContrat?.fichier_pdf
                                            ? "Cliquez pour remplacer le fichier PDF existant"
                                            : "Cliquez pour sélectionner un fichier PDF"}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Formats acceptés : PDF uniquement (jusqu'à 50 Mo)
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {editFichierPdf && (
                            <Button
                                size="small"
                                color="error"
                                onClick={() => {
                                    setEditFichierPdf(null);
                                    document.getElementById("fichier-pdf-edit").value = "";
                                }}
                                sx={{ mt: 1 }}
                            >
                                Annuler le remplacement
                            </Button>
                        )}
                    </Box>
                    {/* Barre de progression upload — visible uniquement pendant l'upload */}
                    {isUploading && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography variant="caption" color="primary" fontWeight={600}>
                                    Upload en cours...
                                </Typography>
                                <Typography variant="caption" color="primary" fontWeight={600}>
                                    {uploadProgress}%
                                </Typography>
                            </Box>
                            <Box sx={{
                                width: "100%",
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                overflow: "hidden",
                            }}>
                                <Box sx={{
                                    height: "100%",
                                    width: `${uploadProgress}%`,
                                    borderRadius: 4,
                                    background: "linear-gradient(90deg, #2a5fa9 0%, #3a7fb5 20%, #4a9fc0 40%, #5ab8a0 60%, #70bd8d 80%, #8fc68b 100%)",
                                    transition: "width 0.3s ease",
                                }} />
                            </Box>
                            {uploadProgress === 100 && (
                                <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                                    Traitement en cours...
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <Divider sx={{ mt: 2 }} />

                <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
                    <Button
                        onClick={() => {
                            setOpenEdit(false);
                            setEditFichierPdf(null); // ── NOUVEAU ──
                        }}
                        sx={{
                            color: theme.palette.error.main,
                            borderRadius: "10px",
                            px: 3,
                            py: 1,
                            fontWeight: 500,
                            "&:hover": {
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                            },
                        }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        disabled={isUploading}
                        variant="contained"
                        color="primary"
                        sx={{
                            borderRadius: "10px",
                            px: 4,
                            py: 1,
                            fontWeight: 600,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                            "&:hover": {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                transform: "translateY(-2px)",
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                            },
                            transition: "all 0.3s ease",
                        }}
                    >
                        {isUploading ? "Upload..." : "Enregistrer"}

                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                    mt: 10,
                    zIndex: 9999,
                }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{
                        width: "100%",
                        minWidth: 320,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        boxShadow: `0 4px 20px ${alpha(
                            snackbarSeverity === "error"
                                ? theme.palette.error.main
                                : theme.palette.success.main,
                            0.3
                        )}`,
                        borderRadius: "12px",
                        py: 1.5,
                        background:
                            snackbarSeverity === "success"
                                ? `linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)`
                                : snackbarSeverity === "error"
                                    ? `linear-gradient(135deg, #f44336 0%, #ef5350 100%)`
                                    : undefined,
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Delete Dialog */}
            <Dialog
                open={openDelete}
                onClose={() => setOpenDelete(false)}
                PaperProps={{
                    sx: {
                        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        minWidth: 450,
                        px: 2,
                        py: 1,
                    },
                }}
            >
                <DialogTitle>
                    <SectionTitle sx={{ justifyContent: 'center', textAlign: 'center' }}>Supprimer Contrat</SectionTitle>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', textAlign: 'center', mt: 1, fontSize: '0.95rem' }}>
                        Voulez-vous vraiment supprimer ce contrat ?
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', textAlign: 'center', mt: 1, fontSize: '0.95rem' }}>
                        Cette action est irréversible.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenDelete(false)}
                        sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                            },
                        }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: '8px',
                            background: alpha(theme.palette.error.main, 0.8),
                            '&:hover': {
                                background: theme.palette.error.main,
                                transform: 'scale(1.05)',
                            },
                        }}
                    >
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancellation Letter Dialog */}
            <Dialog
                open={openLetter}
                onClose={() => setOpenLetter(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    },
                }}
            >
                <DialogTitle>
                    <SectionTitle>Créer une Lettre de Résiliation</SectionTitle>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Nom Fournisseur"
                        fullWidth
                        value={letterContent.recipientName}
                        onChange={(e) => setLetterContent({ ...letterContent, recipientName: e.target.value })}
                        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: '8px' }}
                    />
                    <TextField
                        margin="dense"
                        label="Nom Contact"
                        fullWidth
                        value={letterContent.recipientContact}
                        onChange={(e) => setLetterContent({ ...letterContent, recipientContact: e.target.value })}
                        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: '8px' }}
                    />
                    <TextField
                        margin="dense"
                        label="Adresse Fournisseur"
                        fullWidth
                        value={letterContent.recipientAddress}
                        onChange={(e) => setLetterContent({ ...letterContent, recipientAddress: e.target.value })}
                        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: '8px' }}
                    />
                    <TextField
                        margin="dense"
                        label="Code Postal"
                        fullWidth
                        value={letterContent.recipientCP}
                        onChange={(e) => setLetterContent({ ...letterContent, recipientCP: e.target.value })}
                        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: '8px' }}
                    />
                    <TextField
                        margin="dense"
                        label="Ville"
                        fullWidth
                        value={letterContent.recipientCity}
                        onChange={(e) => setLetterContent({ ...letterContent, recipientCity: e.target.value })}
                        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: '8px' }}
                    />
                    <TextField
                        margin="dense"
                        label="Pays"
                        fullWidth
                        value={letterContent.recipientCountry}
                        onChange={(e) => setLetterContent({ ...letterContent, recipientCountry: e.target.value })}
                        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: '8px' }}
                    />
                    <TextField
                        margin="dense"
                        label="Objet"
                        fullWidth
                        value={letterContent.subject}
                        onChange={(e) => setLetterContent({ ...letterContent, subject: e.target.value })}
                        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: '8px' }}
                    />
                    <TextField
                        margin="dense"
                        label="Corps de la lettre"
                        fullWidth
                        multiline
                        rows={6}
                        value={letterContent.body}
                        onChange={(e) => setLetterContent({ ...letterContent, body: e.target.value })}
                        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: '8px' }}
                    />
                    <TextField
                        margin="dense"
                        label="Expéditeur"
                        fullWidth
                        value={letterContent.sender}
                        onChange={(e) => setLetterContent({ ...letterContent, sender: e.target.value })}
                        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: '8px' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenLetter(false)}
                        sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                            },
                        }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleDownloadLetterPDF}
                        variant="contained"
                        color="primary"
                        sx={{
                            borderRadius: '8px',
                            background: alpha(theme.palette.primary.main, 0.8),
                            '&:hover': {
                                background: theme.palette.primary.main,
                                transform: 'scale(1.05)',
                            },
                        }}
                    >
                        Télécharger PDF
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>


    );

};

export default Contrats;